import type { Hours, OpenInterval, Weekday } from "@glowin/schemas";

/** Pure helpers for the hours editor. Kept out of components so they're testable. */

export const WEEKDAYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const DAY_LABEL: Record<Weekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday"
};

export const EMPTY_HOURS: Hours = { weekly: {}, overrides: [] };

export function intervalsFor(hours: Hours, day: Weekday): OpenInterval[] {
  return hours.weekly[day] ?? [];
}

export function isClosed(hours: Hours, day: Weekday): boolean {
  return intervalsFor(hours, day).length === 0;
}

function withDay(hours: Hours, day: Weekday, intervals: OpenInterval[]): Hours {
  return { ...hours, weekly: { ...hours.weekly, [day]: intervals } };
}

/** Add a shift. Defaults to a lunch service, or an evening one if lunch exists. */
export function addInterval(hours: Hours, day: Weekday): Hours {
  const existing = intervalsFor(hours, day);
  const next: OpenInterval =
    existing.length === 0 ? { open: "11:30", close: "14:30" } : { open: "17:00", close: "21:00" };
  return withDay(hours, day, [...existing, next]);
}

export function removeInterval(hours: Hours, day: Weekday, index: number): Hours {
  return withDay(
    hours,
    day,
    intervalsFor(hours, day).filter((_, i) => i !== index)
  );
}

export function updateInterval(
  hours: Hours,
  day: Weekday,
  index: number,
  patch: Partial<OpenInterval>
): Hours {
  return withDay(
    hours,
    day,
    intervalsFor(hours, day).map((iv, i) => (i === index ? { ...iv, ...patch } : iv))
  );
}

/** Closing a day clears its shifts; opening restores one default shift. */
export function setClosed(hours: Hours, day: Weekday, closed: boolean): Hours {
  if (closed) return withDay(hours, day, []);
  return intervalsFor(hours, day).length === 0 ? addInterval(hours, day) : hours;
}

/** Copy one day's shifts to every other day — the common "same hours all week" case. */
export function copyDayToAll(hours: Hours, from: Weekday): Hours {
  const intervals = intervalsFor(hours, from);
  const weekly = { ...hours.weekly };
  for (const day of WEEKDAYS) weekly[day] = intervals.map((iv) => ({ ...iv }));
  return { ...hours, weekly };
}

/** "11:30" -> "11:30 AM" for display. */
export function to12Hour(hhmm: string): string {
  const [hStr, m] = hhmm.split(":");
  const h = Number(hStr);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${suffix}`;
}

/** Human summary of a day, e.g. "11:30 AM – 2:30 PM, 5:00 PM – 9:00 PM" or "Closed". */
export function describeDay(hours: Hours, day: Weekday): string {
  const intervals = intervalsFor(hours, day);
  if (intervals.length === 0) return "Closed";
  return intervals.map((iv) => `${to12Hour(iv.open)} – ${to12Hour(iv.close)}`).join(", ");
}

/** Problems that would make the server reject, surfaced before saving. */
export function validateHours(hours: Hours): string[] {
  const problems: string[] = [];
  for (const day of WEEKDAYS) {
    const intervals = intervalsFor(hours, day);
    intervals.forEach((iv, i) => {
      if (iv.open === iv.close) {
        problems.push(`${DAY_LABEL[day]}: shift ${i + 1} opens and closes at the same time`);
      }
    });
  }
  return problems;
}

/** Preset pause durations, in minutes. Chosen over a free-text time entry: an owner pausing orders
 * mid-rush wants one tap, and a mistyped absolute time silently keeps the phone shut. */
export const PAUSE_PRESETS: { label: string; minutes: number }[] = [
  { label: "30 minutes", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "Rest of today", minutes: 0 }
];

/** When a preset should expire, from `now`. "Rest of today" ends at local midnight. */
export function pauseUntilIso(minutes: number, now = new Date()): string {
  if (minutes > 0) return new Date(now.getTime() + minutes * 60_000).toISOString();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 0, 0);
  // Already past 23:59 — give a minimum useful pause rather than one in the past, which would
  // read as "paused" in the UI while the agent kept taking orders.
  if (endOfDay <= now) return new Date(now.getTime() + 60 * 60_000).toISOString();
  return endOfDay.toISOString();
}

/** Is a pause currently in force? A past `untilIso` is not a pause. */
export function isPaused(hours: Hours | undefined, now = new Date()): boolean {
  const until = hours?.temporaryClosure?.untilIso;
  return Boolean(until && new Date(until) > now);
}

/** One line for the banner, e.g. "Orders paused until 8:30 PM — Kitchen equipment down". */
export function describePause(hours: Hours | undefined, now = new Date()): string | undefined {
  if (!isPaused(hours, now)) return undefined;
  const c = hours!.temporaryClosure!;
  const time = new Date(c.untilIso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
  return `Orders paused until ${time}${c.reason ? ` — ${c.reason}` : ""}`;
}
